import Vue from 'vue'
import './plugins/axios'
import store from './store'
import VueApollo from 'vue-apollo'
import AWSAppSyncClient from 'aws-appsync'
import Lottie from 'vue-lottie'
import Flag from 'vue-flagpack'
import PortalVue from 'portal-vue'
import {Amplify} from 'aws-amplify'
import Fragment from 'vue-fragment'

// Check app mode — priority: IB > Broker > Trader
const isBrokerStandalone = process.env.VUE_APP_BROKER_STANDALONE === 'true'
const isIBStandalone = process.env.VUE_APP_IB_STANDALONE === 'true'

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.log('App Mode:', isIBStandalone ? 'IB' : isBrokerStandalone ? 'Broker' : 'Trader')
}

// Add app class to body immediately
if (typeof document !== 'undefined') {
  if (isIBStandalone) {
    document.body.classList.add('ib-app')
  } else if (isBrokerStandalone) {
    document.body.classList.add('broker-app')
  }
}

// Per-app modules and styles.
//
// IMPORTANT: the conditions below intentionally repeat the inline
// `process.env.VUE_APP_*` comparisons instead of using the consts above.
// webpack's DefinePlugin turns each comparison into a build-time constant, so
// every webpack version reliably drops the other apps' branches (and their CSS)
// from the bundle. Routing the condition through a variable defeats that
// analysis in some webpack versions, which silently bundles the trader app —
// and its CSS — into broker/IB builds. Deployed and local bundles then differ.
// Design-system CSS shared by all apps lives in src/styles/shared-design.scss,
// imported explicitly by each app root (never leaked across apps).
require('@fortawesome/fontawesome-free/css/all.css')

let App, router, i18n
if (process.env.VUE_APP_IB_STANDALONE === 'true') {
  require('flag-icon-css/css/flag-icons.min.css')
  require('./apps/ib/styles/app.scss')
  App = require('./apps/ib/App.vue').default
  router = require('./apps/ib/router').default
  i18n = require('./apps/ib/i18n').default
} else if (process.env.VUE_APP_BROKER_STANDALONE === 'true') {
  require('flag-icon-css/css/flag-icons.min.css')
  require('./apps/broker/styles/app.scss')
  App = require('./apps/broker/App.vue').default
  router = require('./apps/broker/router').default
  i18n = require('./apps/broker/i18n').default
} else {
  // Trader styles come from App.vue (shared-design + trader-only styles).
  App = require('./App.vue').default
  router = require('./router').default
  i18n = require('./i18n').default
}

// Import Vuetify
import Vuetify from 'vuetify/lib'
Vue.use(Vuetify)

// Create vuetify instance with conditional theme
const vuetify = require('./plugins/vuetify').default

// Conditionally import TranslationHelper for trader app
// (inline env checks so the module is excluded from broker/IB bundles)
if (process.env.VUE_APP_BROKER_STANDALONE !== 'true' && process.env.VUE_APP_IB_STANDALONE !== 'true') {
  const TranslationHelper = require('./mixins/translationHelper').default
  Vue.mixin(TranslationHelper)
}

// Register PortalVue
Vue.use(PortalVue)

// Initialize AWS Amplify library
Amplify.configure({
  Auth: {
    region: 'us-east-1',
    userPoolId: 'us-east-1_LKpAkTLn2',
    userPoolWebClientId: '3vr45vnntgtn2du4for24o5438',
  }
})

// Apollo setup for trader app only
// (inline env checks so the AppSync client is excluded from broker/IB bundles)
let apolloProvider = null
if (process.env.VUE_APP_BROKER_STANDALONE !== 'true' && process.env.VUE_APP_IB_STANDALONE !== 'true') {
  const config = {
    url: 'https://gxd52tpynfcjbghzu7axjnbbk4.appsync-api.us-east-1.amazonaws.com/graphql',
    region: 'us-east-1',
    auth: {
      type: 'AWS_LAMBDA',
      token: '1',
    },
    disableOffline: true
  }

  const options = {
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network'
      }
    }
  }

  const client = new AWSAppSyncClient(config, options)
  apolloProvider = new VueApollo({
    defaultClient: client
  })
  Vue.use(VueApollo)
}

Vue.config.productionTip = false

// Global context flags
Vue.prototype.$isTraderApp = !isBrokerStandalone && !isIBStandalone
Vue.prototype.$isBrokerApp = isBrokerStandalone
Vue.prototype.$isIBApp = isIBStandalone

// Register global components
Vue.component('lottie', Lottie)
Vue.use(Flag, {name: 'Flag'})
Vue.use(Fragment.Plugin)

// Create Vue instance with conditional apollo
const vueOptions = {
  router,
  vuetify,
  store,
  i18n,
  render: h => h(App)
}

// Only add apollo for trader app
if (apolloProvider) {
  vueOptions.apolloProvider = apolloProvider
}

new Vue(vueOptions).$mount('#app')